/**
 * How you get in, in two rows — Aaron's review, with screenshots of an app he
 * prefers: "two simple lines for password and mfa with chips for which MFA is
 * currently enabled."
 *
 * It used to be six rows — a password, one per method, and recovery codes —
 * each with its own verb. That is the right shape for a screen *about*
 * two-step and the wrong shape for a profile, where two-step is one of five
 * cards and somebody is usually passing through. Six rows of state made the
 * card the loudest thing on the page and buried the one line that matters:
 * is it on, and with what.
 *
 * So the detail moved to where it was already going. **Manage** opens the
 * setup dialog (`two-step-setup.tsx`), which is the same dialog the first-time
 * walkthrough uses — one screen for adding, replacing, removing and
 * regenerating, rather than two places that each do some of it.
 *
 * What stays here is what a glance is for: when the password last changed, and
 * a chip per method that is on. A chip is the right control because the answer
 * is a *list* — "3 methods" alone cannot tell somebody their passkey is the
 * one that is missing.
 */
import { MFA_ORDER, MFA_KIND_INFO, type MfaKind, type MfaMethod, type MfaPolicy } from '@rowted/shared'
import { SettingsCard, SettingRow } from './settings-card.js'
import { Button } from './primitives.js'
import { IconCheck, IconLock, IconSecurity } from '../frame/icons.js'
import { cn } from '../lib/cn.js'
import { formatDocumentDate, formatNumber } from '../lib/format.js'

export interface MethodsAnswer {
  methods: MfaMethod[]
  policy: MfaPolicy
  destinations: { email: string; phone: string | null }
  wantsAnother: boolean
  /**
   * Which kinds this door accepts, when the side says.
   *
   * Rowted Admin takes a passkey and an authenticator and cannot take a code
   * by email or text — `MFA_ADMIN_KINDS`, and the honest reason is in
   * `shared/src/mfa.ts`. Absent means all four, which is the tenant side.
   */
  kinds?: readonly MfaKind[]
}

export function SignInMethods({
  answer, recoveryCodesLeft, passwordChangedAt, onChangePassword, onManage,
}: {
  answer: MethodsAnswer | null
  recoveryCodesLeft: number
  passwordChangedAt: string | null
  onChangePassword: () => void
  /** Opens the setup dialog, which is where every method is actually changed. */
  onManage: () => void
}) {
  /*
   * `null` is "not known yet", and it stays null when the read fails. The row
   * says "—" rather than "Off", because telling somebody who holds a passkey
   * that they hold nothing is worse than no answer at all.
   */
  const on = answer?.methods ?? null
  const count = on?.length ?? 0

  return (
    <SettingsCard title="Security" sub="your password and how you prove it is you">
      <SettingRow
        icon={<IconLock size={17} />}
        label="Password"
        sub="12+ characters, checked against known breaches"
        /*
         * Nothing, rather than "not set", when there is no date. `users
         * .password_changed_at` is written when a password is *changed*, so
         * every account seeded or created before it existed reads null while
         * holding a perfectly good hash — and telling somebody who just
         * signed in with their password that they have not set one is the
         * kind of wrong a person acts on.
         */
        value={passwordChangedAt
          ? `changed ${formatDocumentDate(passwordChangedAt)}`
          : undefined}
        action={<Button className="h-[30px]" onClick={onChangePassword}>Change</Button>}
      />

      <SettingRow
        icon={<IconSecurity size={17} />}
        label="Two-step sign-in"
        sub={on === null
          ? 'a second step after your password'
          : (
            <MethodChips
              methods={on}
              /* Only what this door takes. Rowted Admin cannot check an email
                 code, so a chip for one would offer a method the sign-in
                 refuses — the same rule the dialog's cards follow. */
              kinds={answer?.kinds ?? MFA_ORDER}
              recoveryCodesLeft={recoveryCodesLeft}
            />
          )}
        value={on === null
          ? '—'
          : count === 0
            ? 'Off'
            /* The count beside the chips, because the chips are what is on and
               the number is how close they are to the two §4.3 asks for. */
            : `${formatNumber(count)} ${count === 1 ? 'method' : 'methods'}`}
        tone={count > 0 ? 'good' : 'plain'}
        action={(
          <Button className="h-[30px]" onClick={onManage}>
            {count === 0 ? 'Set up' : 'Manage'}
          </Button>
        )}
      />
    </SettingsCard>
  )
}

/**
 * One chip per kind, on or off.
 *
 * **Every kind, not only the ones that are on.** A row of three green chips
 * cannot tell somebody that the fourth exists, and the thing a person comes to
 * this card to find out is usually what they have *not* set up. An off chip is
 * a quiet outline; an on chip is tinted and carries a tick.
 */
function MethodChips({ methods, kinds, recoveryCodesLeft }: {
  methods: MfaMethod[]
  kinds: readonly MfaKind[]
  recoveryCodesLeft: number
}) {
  const held = new Set(methods.map((one) => one.kind))

  return (
    <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
      {kinds.map((kind) => {
        const on = held.has(kind)
        return (
          <span
            key={kind}
            className={cn('inline-flex h-[22px] items-center gap-1 rounded-chip border px-2',
              'text-[11.5px] font-semibold',
              on
                ? 'border-accent-line bg-accent-soft text-accent-dark'
                : 'border-line bg-surface text-text-faint')}
          >
            {on && <IconCheck size={11} />}
            {MFA_KIND_INFO[kind].name}
          </span>
        )
      })}
      {methods.length > 0 && (
        /*
         * Recovery codes are not a method you turn on, so they are not a chip
         * — but how many are left belongs on the same line, because it is the
         * one number that decides whether somebody can get back in when every
         * chip above has failed. It turns amber before it turns into a
         * problem.
         */
        <span className={cn('text-[11.5px]',
          recoveryCodesLeft > 2 ? 'text-text-faint' : 'font-semibold text-warn')}>
          · {formatNumber(recoveryCodesLeft)} of 10 recovery codes left
        </span>
      )}
    </span>
  )
}
